import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import analyticsService, {
  DashboardMetrics,
  TimeSeriesData,
  AgentMetrics,
  SlaMetrics,
  DateRange,
  TimeSeriesResponse,
  PeriodComparison,
  TrendAnalysis,
  Anomaly,
  CustomReport,
  CreateReportRequest,
  UpdateReportRequest,
  ReportExecutionResult,
  ScheduleReportRequest,
  ReportSchedule,
  CsatStats,
  ChatStats,
  AutomationRuleStats,
  AutomationExecutionStats,
  CustomerStatusCounts,
  SlaBreachStats,
  AiChatbotAnalytics,
  PipelineInfo,
  InsightsResponse,
  NLQueryResponse,
  VolumeForecast,
  SlaBreachPrediction,
  WorkloadPrediction,
  AnomalyDetectionResult,
  CustomerHealthResponse,
  AgentWellnessResponse,
  CostAnalyticsResponse,
  ActivityHeatmapResponse,
  DeflectionFunnelResponse,
  RecentActivity,
} from '../services/analytics.service';

// ==================== Query Keys ====================

export const analyticsKeys = {
  all: ['analytics'] as const,
  dashboard: (dateRange?: DateRange, countsAllTime?: boolean, teamId?: string) =>
    [...analyticsKeys.all, 'dashboard', dateRange?.start, dateRange?.end, countsAllTime ?? false, teamId] as const,
  ticketTrends: (period: string, dateRange?: DateRange, teamId?: string) =>
    [...analyticsKeys.all, 'trends', period, dateRange?.start, dateRange?.end, teamId] as const,
  agents: (dateRange?: DateRange, reportView?: boolean, teamId?: string) =>
    [...analyticsKeys.all, 'agents', dateRange?.start, dateRange?.end, reportView ? 'report' : undefined, teamId] as const,
  teamPerformance: (dateRange?: DateRange) =>
    [...analyticsKeys.all, 'teamPerformance', dateRange?.start, dateRange?.end] as const,
  channels: (dateRange?: DateRange) =>
    [...analyticsKeys.all, 'channels', dateRange?.start, dateRange?.end] as const,
  sla: (dateRange?: DateRange, teamId?: string) =>
    [...analyticsKeys.all, 'sla', dateRange?.start, dateRange?.end, teamId] as const,
  // Time-series keys
  timeSeries: (metric: string, interval?: string) =>
    [...analyticsKeys.all, 'timeSeries', metric, interval] as const,
  periodComparison: (metric: string) =>
    [...analyticsKeys.all, 'comparison', metric] as const,
  trends: (metrics: string[], days?: number) =>
    [...analyticsKeys.all, 'multiTrends', ...metrics, days] as const,
  trendAnalysis: (metric: string, periodType?: string) =>
    [...analyticsKeys.all, 'trendAnalysis', metric, periodType] as const,
  anomalies: (metric: string) =>
    [...analyticsKeys.all, 'anomalies', metric] as const,
  // Reports keys
  reports: () => [...analyticsKeys.all, 'reports'] as const,
  report: (id: string) => [...analyticsKeys.all, 'reports', id] as const,
  generatedReports: (reportType?: string) =>
    [...analyticsKeys.all, 'generatedReports', reportType] as const,
  generatedReport: (id: string) =>
    [...analyticsKeys.all, 'generatedReport', id] as const,
  reportMetrics: (dataSource?: string) =>
    [...analyticsKeys.all, 'reportMetrics', dataSource] as const,
  reportDimensions: () => [...analyticsKeys.all, 'reportDimensions'] as const,
  chartTypes: () => [...analyticsKeys.all, 'chartTypes'] as const,
  // Cross-service keys
  csat: (dateRange?: DateRange) =>
    [...analyticsKeys.all, 'csat', dateRange?.start, dateRange?.end] as const,
  chatStats: () => [...analyticsKeys.all, 'chatStats'] as const,
  automationRules: () => [...analyticsKeys.all, 'automationRules'] as const,
  automationExecutions: () => [...analyticsKeys.all, 'automationExecutions'] as const,
  customers: () => [...analyticsKeys.all, 'customers'] as const,
  slaBreaches: (dateRange?: DateRange) =>
    [...analyticsKeys.all, 'slaBreaches', dateRange?.start, dateRange?.end] as const,
  aiChatbot: (days?: number) => [...analyticsKeys.all, 'aiChatbot', days] as const,
  pipelines: () => [...analyticsKeys.all, 'pipelines'] as const,
  // AI analytics keys
  aiInsights: (dateRange?: DateRange) =>
    [...analyticsKeys.all, 'aiInsights', dateRange?.start, dateRange?.end] as const,
  nlQuery: (query: string) =>
    [...analyticsKeys.all, 'nlQuery', query] as const,
  volumeForecast: (horizon?: number) =>
    [...analyticsKeys.all, 'volumeForecast', horizon] as const,
  slaPrediction: () => [...analyticsKeys.all, 'slaPrediction'] as const,
  workloadPrediction: () => [...analyticsKeys.all, 'workloadPrediction'] as const,
  anomalyDetection: (metric: string) =>
    [...analyticsKeys.all, 'anomalyDetection', metric] as const,
  // Health score keys
  customerHealth: (limit?: number) =>
    [...analyticsKeys.all, 'customerHealth', limit] as const,
  agentWellness: () => [...analyticsKeys.all, 'agentWellness'] as const,
  costAnalytics: () => [...analyticsKeys.all, 'costAnalytics'] as const,
  activityHeatmap: () => [...analyticsKeys.all, 'activityHeatmap'] as const,
  deflectionFunnel: () => [...analyticsKeys.all, 'deflectionFunnel'] as const,
};

const STALE_TIME = 5 * 60 * 1000; // 5 minutes

// ==================== Individual Hooks ====================

/** `teamId`: a Team filter — figures for that team and its sub-teams. */
export function useDashboardMetrics(dateRange?: DateRange, countsAllTime = false, teamId?: string) {
  return useQuery({
    queryKey: analyticsKeys.dashboard(dateRange, countsAllTime, teamId),
    queryFn: () => analyticsService.getDashboardMetrics(dateRange, countsAllTime, teamId),
    staleTime: STALE_TIME,
  });
}

export function useTicketTrends(
  period: 'day' | 'week' | 'month',
  dateRange?: DateRange,
  teamId?: string,
) {
  return useQuery({
    queryKey: analyticsKeys.ticketTrends(period, dateRange, teamId),
    queryFn: () =>
      analyticsService.getTicketTrends(period, dateRange || { start: '', end: '' }, teamId),
    staleTime: STALE_TIME,
    enabled: !!dateRange,
  });
}

export function useAgentMetrics(dateRange?: DateRange, opts?: { reportView?: boolean; teamId?: string }) {
  return useQuery({
    queryKey: analyticsKeys.agents(dateRange, opts?.reportView, opts?.teamId),
    queryFn: () => analyticsService.getAgentMetrics(dateRange, opts),
    staleTime: STALE_TIME,
  });
}

export function useChannelMetrics(dateRange?: DateRange) {
  return useQuery({
    queryKey: analyticsKeys.channels(dateRange),
    queryFn: () => analyticsService.getChannelMetrics(dateRange),
    staleTime: STALE_TIME,
  });
}

export function useSlaMetrics(dateRange?: DateRange, teamId?: string) {
  return useQuery({
    queryKey: analyticsKeys.sla(dateRange, teamId),
    queryFn: () => analyticsService.getSlaMetrics(dateRange, teamId),
    staleTime: STALE_TIME,
  });
}

/** Reports → Team Performance rows for the period. */
export function useTeamPerformance(dateRange?: DateRange, enabled = true) {
  return useQuery({
    queryKey: analyticsKeys.teamPerformance(dateRange),
    queryFn: () => analyticsService.getTeamPerformance(dateRange),
    staleTime: STALE_TIME,
    enabled,
  });
}

export function useRecentActivity() {
  return useQuery({
    queryKey: [...analyticsKeys.all, 'recentActivity'] as const,
    queryFn: () => analyticsService.getRecentActivity(),
    staleTime: STALE_TIME,
  });
}

// ==================== Time-Series Hooks ====================

export function useTimeSeries(
  metricName: string,
  options?: { startDate?: string; endDate?: string; interval?: string },
) {
  return useQuery({
    queryKey: analyticsKeys.timeSeries(metricName, options?.interval),
    queryFn: () => analyticsService.getTimeSeries(metricName, options),
    staleTime: STALE_TIME,
    enabled: !!metricName,
  });
}

export function usePeriodComparison(
  metricName: string,
  currentRange?: DateRange,
  compareRange?: DateRange,
) {
  return useQuery({
    queryKey: analyticsKeys.periodComparison(metricName),
    queryFn: () => analyticsService.comparePeriods(metricName, currentRange!, compareRange!),
    staleTime: STALE_TIME,
    enabled: !!metricName && !!currentRange && !!compareRange,
  });
}

export function useMultiTrends(metrics: string[], days?: number) {
  return useQuery({
    queryKey: analyticsKeys.trends(metrics, days),
    queryFn: () => analyticsService.getTrends(metrics, days),
    staleTime: STALE_TIME,
    enabled: metrics.length > 0,
  });
}

export function useTrendAnalysis(
  metricName: string,
  options?: { periodType?: string; lookbackPeriods?: number },
) {
  return useQuery({
    queryKey: analyticsKeys.trendAnalysis(metricName, options?.periodType),
    queryFn: () => analyticsService.analyzeTrend(metricName, options),
    staleTime: STALE_TIME,
    enabled: !!metricName,
  });
}

export function useAnomalyDetection(
  metricName: string,
  options?: { thresholdStddev?: number; lookbackDays?: number },
) {
  return useQuery({
    queryKey: analyticsKeys.anomalies(metricName),
    queryFn: () => analyticsService.detectAnomalies(metricName, options),
    staleTime: STALE_TIME,
    enabled: !!metricName,
  });
}

// ==================== Cross-Service Analytics Hooks ====================

export function useCsatStats(dateRange?: DateRange) {
  return useQuery({
    queryKey: analyticsKeys.csat(dateRange),
    queryFn: () => analyticsService.getCsatStats(dateRange),
    staleTime: STALE_TIME,
  });
}

// Bug #9700: called unconditionally on every Reports page load regardless of
// which tab was active, so it fired (and could fail/404 in a stale
// deployment) even when the Live Chat tab was never opened. `enabled`
// defaults to true so every other call site keeps today's behavior exactly.
export function useChatStats(enabled: boolean = true) {
  return useQuery({
    queryKey: analyticsKeys.chatStats(),
    queryFn: () => analyticsService.getChatStats(),
    staleTime: STALE_TIME,
    enabled,
  });
}

export function useAutomationRuleStats() {
  return useQuery({
    queryKey: analyticsKeys.automationRules(),
    queryFn: () => analyticsService.getAutomationRuleStats(),
    staleTime: STALE_TIME,
  });
}

export function useAutomationExecutionStats() {
  return useQuery({
    queryKey: analyticsKeys.automationExecutions(),
    queryFn: () => analyticsService.getAutomationExecutionStats(),
    staleTime: STALE_TIME,
  });
}

export function useCustomerStatusCounts() {
  return useQuery({
    queryKey: analyticsKeys.customers(),
    queryFn: () => analyticsService.getCustomerStatusCounts(),
    staleTime: STALE_TIME,
  });
}

export function useSlaBreachStats(dateRange?: DateRange) {
  return useQuery({
    queryKey: analyticsKeys.slaBreaches(dateRange),
    queryFn: () => analyticsService.getSlaBreachStats(dateRange),
    staleTime: STALE_TIME,
  });
}

export function useAiChatbotAnalytics(days?: number) {
  return useQuery({
    queryKey: analyticsKeys.aiChatbot(days),
    queryFn: () => analyticsService.getAiChatbotAnalytics(days),
    staleTime: STALE_TIME,
  });
}

export function usePipelines() {
  return useQuery({
    queryKey: analyticsKeys.pipelines(),
    queryFn: () => analyticsService.getPipelines(),
    staleTime: 10 * 60 * 1000, // 10 min - pipelines change rarely
  });
}

// ==================== Reports Hooks ====================

export function useReportMetrics(dataSource?: string) {
  return useQuery({
    queryKey: analyticsKeys.reportMetrics(dataSource),
    queryFn: () => analyticsService.getReportMetrics(dataSource),
    staleTime: 10 * 60 * 1000, // 10 minutes - metadata changes infrequently
  });
}

export function useReportDimensions() {
  return useQuery({
    queryKey: analyticsKeys.reportDimensions(),
    queryFn: () => analyticsService.getReportDimensions(),
    staleTime: 10 * 60 * 1000,
  });
}

export function useChartTypes() {
  return useQuery({
    queryKey: analyticsKeys.chartTypes(),
    queryFn: () => analyticsService.getChartTypes(),
    staleTime: 10 * 60 * 1000,
  });
}

export function useReportsList(includePublic?: boolean) {
  return useQuery({
    queryKey: analyticsKeys.reports(),
    queryFn: () => analyticsService.listReports(includePublic),
    staleTime: STALE_TIME,
  });
}

export function useReport(reportId: string) {
  return useQuery({
    queryKey: analyticsKeys.report(reportId),
    queryFn: () => analyticsService.getReport(reportId),
    staleTime: STALE_TIME,
    enabled: !!reportId,
  });
}

// Scheduler-generated report snapshots (only jobs with "Show on Reports page").
// Always refetch when the modal opens/mounts so a report that was just run
// (from the Scheduled Jobs page) shows up without a full page reload.
export function useGeneratedReports(reportType?: string, enabled = true) {
  return useQuery({
    queryKey: analyticsKeys.generatedReports(reportType),
    queryFn: () => analyticsService.listGeneratedReports(reportType),
    staleTime: 0,
    refetchOnMount: 'always',
    enabled,
  });
}

export function useGeneratedReport(id: string | null) {
  return useQuery({
    queryKey: analyticsKeys.generatedReport(id || ''),
    queryFn: () => analyticsService.getGeneratedReport(id as string),
    staleTime: STALE_TIME,
    enabled: !!id,
  });
}

export function useCreateReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateReportRequest) => analyticsService.createReport(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: analyticsKeys.reports() });
    },
  });
}

export function useUpdateReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateReportRequest }) =>
      analyticsService.updateReport(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: analyticsKeys.reports() });
      queryClient.invalidateQueries({ queryKey: analyticsKeys.report(id) });
    },
  });
}

export function useDeleteReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => analyticsService.deleteReport(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: analyticsKeys.reports() });
    },
  });
}

export function useRunReport() {
  return useMutation({
    mutationFn: ({ id, dateRange }: { id: string; dateRange?: DateRange }) =>
      analyticsService.runReport(id, dateRange),
  });
}

export function useScheduleReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ScheduleReportRequest }) =>
      analyticsService.scheduleReport(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: analyticsKeys.report(id) });
    },
  });
}

export function useDuplicateReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      analyticsService.duplicateReport(id, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: analyticsKeys.reports() });
    },
  });
}

// ==================== Combined Hook (backward compatible) ====================

export interface UseAnalyticsReturn {
  dashboardMetrics: DashboardMetrics | null;
  ticketTrends: TimeSeriesData[];
  agentMetrics: AgentMetrics[];
  channelMetrics: Record<string, { volume: number; avgResponseTime: number }> | null;
  slaMetrics: SlaMetrics | null;
  loading: boolean;
  error: string | null;
  dateRange: DateRange | null;
  setDateRange: (range: DateRange | null) => void;
  loadDashboard: () => Promise<void>;
  loadTicketTrends: (period: 'day' | 'week' | 'month') => Promise<void>;
  loadAgentMetrics: () => Promise<void>;
  loadChannelMetrics: () => Promise<void>;
  loadSlaMetrics: () => Promise<void>;
  refreshAll: () => Promise<void>;
}

export function useAnalytics(): UseAnalyticsReturn {
  const [dateRange, setDateRange] = useState<DateRange | null>(null);
  const queryClient = useQueryClient();

  const dashboard = useDashboardMetrics(dateRange || undefined);
  const agents = useAgentMetrics(dateRange || undefined);
  const channels = useChannelMetrics(dateRange || undefined);
  const sla = useSlaMetrics(dateRange || undefined);

  const loading = dashboard.isLoading || agents.isLoading || channels.isLoading || sla.isLoading;
  const error = dashboard.error?.message || agents.error?.message || channels.error?.message || sla.error?.message || null;

  const refreshAll = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: analyticsKeys.all });
  }, [queryClient]);

  // Keep backward-compatible imperative methods
  const loadDashboard = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: analyticsKeys.dashboard(dateRange || undefined) });
  }, [queryClient, dateRange]);

  const loadTicketTrends = useCallback(async (_period: 'day' | 'week' | 'month' = 'day') => {
    // Trends are loaded via useTicketTrends hook directly
  }, []);

  const loadAgentMetrics = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: analyticsKeys.agents(dateRange || undefined) });
  }, [queryClient, dateRange]);

  const loadChannelMetrics = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: analyticsKeys.channels(dateRange || undefined) });
  }, [queryClient, dateRange]);

  const loadSlaMetrics = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: analyticsKeys.sla(dateRange || undefined) });
  }, [queryClient, dateRange]);

  return {
    dashboardMetrics: dashboard.data ?? null,
    ticketTrends: [],
    agentMetrics: agents.data ?? [],
    channelMetrics: channels.data ?? null,
    slaMetrics: sla.data ?? null,
    loading,
    error,
    dateRange,
    setDateRange,
    loadDashboard,
    loadTicketTrends,
    loadAgentMetrics,
    loadChannelMetrics,
    loadSlaMetrics,
    refreshAll,
  };
}

// ==================== AI Analytics Hooks ====================

/** Generate AI insights for dashboard metrics. Call imperatively via mutate(). */
export function useGenerateInsights() {
  return useMutation({
    mutationFn: (params: { metrics: DashboardMetrics; previousMetrics?: DashboardMetrics }) =>
      analyticsService.getDashboardInsights(params.metrics, params.previousMetrics),
  });
}

/** Natural language analytics query. Returns on-demand via mutate(). */
export function useNLQuery() {
  return useMutation({
    mutationFn: (
      args: string | { query: string; sessionId?: string; refresh?: boolean },
    ) => {
      if (typeof args === 'string') {
        return analyticsService.naturalLanguageQuery(args);
      }
      return analyticsService.naturalLanguageQuery(args.query, {
        sessionId: args.sessionId,
        refresh: args.refresh,
      });
    },
  });
}

/** Predict ticket volume from historical trend data. */
export function useVolumeForecast() {
  return useMutation({
    mutationFn: (params: { history: TimeSeriesData[]; horizonDays?: number }) =>
      analyticsService.predictVolume(params.history, params.horizonDays),
  });
}

/** Predict SLA breaches. */
export function useSlaPrediction() {
  return useMutation({
    mutationFn: (params: { slaData: any; volumeForecast?: any[] }) =>
      analyticsService.predictSlaBreaches(params.slaData, params.volumeForecast),
  });
}

/** Predict agent workload distribution. */
export function useWorkloadPrediction() {
  return useMutation({
    mutationFn: (params: { agentData: any[]; volumeForecast?: any[] }) =>
      analyticsService.predictWorkload(params.agentData, params.volumeForecast),
  });
}

/** Detect anomalies in time-series data. */
export function useAnomalyDetectionMutation() {
  return useMutation({
    mutationFn: (params: { timeSeries: Array<{ period: string; value: number }>; metricName?: string }) =>
      analyticsService.detectAnomaliesAi(params.timeSeries, params.metricName),
  });
}

// ==================== Health Score Hooks ====================

/** Fetch customer health scores ranked by risk. */
export function useCustomerHealth(limit?: number) {
  return useQuery({
    queryKey: analyticsKeys.customerHealth(limit),
    queryFn: () => analyticsService.getCustomerHealth(limit),
    staleTime: STALE_TIME,
  });
}

/** Compute agent wellness scores from agent performance data. */
export function useAgentWellness() {
  return useMutation({
    mutationFn: (agentMetrics: AgentMetrics[]) =>
      analyticsService.getAgentWellness(agentMetrics),
  });
}

/** Compute cost analytics from dashboard metrics. */
export function useCostAnalytics() {
  return useMutation({
    mutationFn: (params: { metrics: DashboardMetrics; aiAnalytics?: any }) =>
      analyticsService.getCostAnalytics(params.metrics, params.aiAnalytics),
  });
}

/** Fetch activity heatmap (7x24 hour-by-day grid). */
export function useActivityHeatmap() {
  return useQuery({
    queryKey: analyticsKeys.activityHeatmap(),
    queryFn: () => analyticsService.getActivityHeatmap(),
    staleTime: STALE_TIME,
  });
}

/** Compute deflection funnel from metrics + AI/KB analytics. */
export function useDeflectionFunnel() {
  return useMutation({
    mutationFn: (params: { metrics: DashboardMetrics; aiAnalytics?: any; kbAnalytics?: any }) =>
      analyticsService.getDeflectionFunnel(params.metrics, params.aiAnalytics, params.kbAnalytics),
  });
}

export default useAnalytics;
